import { Field, FieldProps, Form, Formik } from 'formik';
import { useState } from 'react';

import * as yup from 'yup';
import { checkUserExists } from '../../../lib/api/portal-api/user/checkExists';
import Button from '../../atoms/Button';
import TextField from '../../atoms/TextInput';
import PasswordField from '../PasswordField/PasswordField';

import {
  DoubleColumnFields,
  DoubleColumnFieldWrapper,
  FieldWrapper,
  RegisterFormContainer,
} from './register.styled';
import { SelectWrapper } from '../SDMContactForm/SDMContactForm.styled';
import { FieldLabel } from '../ContactUsForm/ContactUsForm.styled';
import { StyledErrorMessage } from '../../atoms/TextInput/styled';

/*TODO: We should lift the <Formik> component up to the parent. This will make it easier to set network errors and test in storybook using Formik Addon
https://github.com/bbbtech/storybook-formik
*/

interface RegisterFormProps {
  onSubmit: (values: RegisterFormSchema) => void;
  initialValues?: RegisterFormSchema;
  buttonLabel?: string;
}
export interface RegisterFormSchema {
  username: string;
  email: string;
  companyName: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  salesRep?: string;
}

const registerFormSchema: yup.SchemaOf<RegisterFormSchema> = yup
  .object()
  .shape({
    username: yup
      .string()
      .required('Username is required')
      .matches(/^[a-zA-Z\_\-0-9]{6,64}$/, {
        excludeEmptyString: false,
        message: `Please enter a valid username. 
          Must be at least 6 characters. 
          Letters, numbers, dashes, and underscores only.`,
      })
      .test(
        'usernameExists',
        'Username already exists',
        async function (value) {
          try {
            if (!value) {
              return false;
            }

            try {
              const usernameExists = await checkUserExists(value);

              if (usernameExists) {
                return this.createError({
                  message: 'Username already exists',
                  path: 'username',
                });
              }

              return true;
            } catch (e) {
              console.log(e);
              return false;
            }
          } catch (e) {
            console.log(e);
            return false;
          }
        }
      ),
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required')
      .test('emailExists', 'Email is invalid', async function (value) {
        try {
          if (!value) {
            return false;
          }

          try {
            const exists = await checkUserExists(undefined, value);

            if (exists) {
              return this.createError({
                message: 'Email already exists',
                path: 'email',
              });
            }

            return true;
          } catch (e) {
            //TODO: handle error
            console.log(e);
            return false;
          }
        } catch (e) {
          console.log(e);
          return false;
        }
      }),
    companyName: yup.string().required('Company Name is required'),
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    password: yup.string().required('Password is required'),
    salesRep: yup.string().optional(),
    confirmPassword: yup
      .string()
      .required('Please confirm password')
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  });

const RegisterForm = (props: RegisterFormProps) => {
  const [showingPassword, setShowingPassword] = useState(false);

  return (
    <RegisterFormContainer>
      <Formik
        onSubmit={(values: RegisterFormSchema) => {
          props.onSubmit(values);
        }}
        initialValues={
          props.initialValues ?? {
            username: '',
            email: '',
            companyName: '',
            firstName: '',
            lastName: '',
            password: '',
            confirmPassword: '',
            salesRep: 'None Selected',
          }
        }
        validationSchema={registerFormSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ errors, isSubmitting }) => (
          <Form>
            <FieldWrapper>
              <Field name='username'>
                {(field: FieldProps) => (
                  <TextField
                    name='username'
                    size={'lg'}
                    {...field}
                    placeholder={'Set Your Username'}
                    type={'text'}
                    errorMessage={errors.username}
                  />
                )}
              </Field>
            </FieldWrapper>
            <FieldWrapper>
              <Field name='email'>
                {(field: FieldProps) => (
                  <TextField
                    name='email'
                    size={'lg'}
                    {...field}
                    placeholder={'Your Email'}
                    type={'text'}
                    errorMessage={errors.email}
                  />
                )}
              </Field>
            </FieldWrapper>
            <FieldWrapper>
              <Field name='companyName'>
                {(field: FieldProps) => (
                  <TextField
                    name='companyName'
                    size={'lg'}
                    {...field}
                    placeholder={'Company Name'}
                    type={'text'}
                    errorMessage={errors.companyName}
                  />
                )}
              </Field>
            </FieldWrapper>

            <DoubleColumnFields>
              <DoubleColumnFieldWrapper>
                <Field name='firstName'>
                  {(field: FieldProps) => (
                    <TextField
                      {...field}
                      name='firstName'
                      size={'lg'}
                      placeholder={'First Name'}
                      type={'text'}
                      errorMessage={errors.firstName}
                    />
                  )}
                </Field>
              </DoubleColumnFieldWrapper>
              <DoubleColumnFieldWrapper>
                <Field name='lastName'>
                  {(field: FieldProps) => (
                    <TextField
                      name='lastName'
                      size={'lg'}
                      {...field}
                      placeholder={'Last Name'}
                      type={'text'}
                      errorMessage={errors.lastName}
                    />
                  )}
                </Field>
              </DoubleColumnFieldWrapper>
            </DoubleColumnFields>
            <FieldWrapper>
              <Field name='password'>
                {(field: FieldProps) => (
                  <PasswordField
                    {...field}
                    name='password'
                    size={'lg'}
                    placeholder={'Set Password'}
                    isEnabled={showingPassword}
                    onIconClick={() => {
                      setShowingPassword(!showingPassword);
                    }}
                    errorMessage={errors.password}
                  />
                )}
              </Field>
            </FieldWrapper>
            <FieldWrapper>
              <Field name='confirmPassword'>
                {(field: FieldProps) => (
                  <PasswordField
                    {...field}
                    name='confirmPassword'
                    size={'lg'}
                    placeholder={'Confirm Password'}
                    isEnabled={showingPassword}
                    onIconClick={() => {
                      setShowingPassword(!showingPassword);
                    }}
                    errorMessage={errors.confirmPassword}
                  />
                )}
              </Field>
            </FieldWrapper>
            <FieldWrapper style={{ flexDirection: 'column' }}>
              {/* <FieldLabel>Who is your sales representative?</FieldLabel> */}
              <SelectWrapper>
                {errors.salesRep && (
                  <StyledErrorMessage>{errors.salesRep}</StyledErrorMessage>
                )}
                <Field name='salesRep'>
                  {(field: FieldProps) => (
                    <select
                      onChange={(e) =>
                        field.form.setFieldValue('salesRep', e.target.value)
                      }
                      value={field.field.value}
                    >
                      <option value='None Selected'>
                        Who is your sales representative?
                      </option>
                      <option value='Bret Anderson'>Bret Anderson</option>
                      <option value='Cameron Richter'>Cameron Richter</option>
                      <option value='Connor Miller'>Connor Miller</option>
                      <option value='Cort Dixon'>Cort Dixon</option>
                      <option value='Danny Youngerman'>Danny Youngerman</option>
                      <option value="Ken D'Arterio">Ken D&rsquo;Arterio</option>
                      <option value='Lewis Jacobson'>Lewis Jacobson</option>
                      <option value='Matt Brandon'>Matt Brandon</option>
                      <option value='Mike Putman'>Mike Putman</option>
                      <option value='Scott Gould'>Scott Gould</option>
                      <option value='Seth Anderson'>Seth Anderson</option>
                      <option value='Seth Barcus'>Seth Barcus</option>
                      <option value='Troy Iverson'>Troy Iverson</option>
                    </select>
                  )}
                </Field>
              </SelectWrapper>
            </FieldWrapper>
            <Button
              variant='primary'
              type='submit'
              size='lg'
              block
              disabled={isSubmitting}
            >
              {props.buttonLabel ?? 'CONTINUE'}
            </Button>
          </Form>
        )}
      </Formik>
    </RegisterFormContainer>
  );
};

export default RegisterForm;
